"use client";
import { createContext as e, useContext as t, useSyncExternalStore as n } from "react";
import { xoroshiro128plus as r } from "pure-rand/generator/xoroshiro128plus";
//#region src/core/currency.ts
var i = {
	name: "credit",
	namePlural: "credits",
	symbol: "$",
	multiplier: 100,
	iconSvg: ""
};
function a(e) {
	switch (e) {
		case 1: return 0;
		case 10: return 1;
		case 100: return 2;
		case 1e3: return 3;
	}
}
var o = [
	0,
	1,
	2,
	3
].map((e) => new Intl.NumberFormat("en-US", {
	minimumFractionDigits: e,
	maximumFractionDigits: e
}));
function s(e, t, n = {}) {
	if (!Number.isFinite(e)) throw RangeError(`formatAmount: amount is not a finite number of cents: ${e}`);
	let r = a(t.multiplier), i = Math.abs(e) / t.multiplier, s = e < 0 ? "-" : "", c = n.compact && i >= 1e3 ? `${(i / 1e3).toFixed(1)}k` : o[r].format(i);
	return `${s}${t.symbol}${c}`;
}
function c(e, t) {
	return e === t.multiplier ? t.name : t.namePlural;
}
function l(e, t) {
	let n = s(e, t);
	return t.symbol === "" ? `${n} ${c(e, t)}` : n;
}
//#endregion
//#region src/i18n/strings.ts
var u = {
	open: "Open",
	openAgain: "Open again",
	skip: "Skip",
	muteSounds: "Mute sounds",
	quantity: "Quantity",
	quickReveal: "Quick reveal",
	claim: "Claim items",
	claiming: "Claiming…",
	sellBack: "Sell back",
	sellingBack: "Selling back…",
	errorSellBackFailed: "Selling back failed. Please try again.",
	withdrawItem: "Withdraw",
	withdrawRequested: "Withdrawal started",
	limitedItemNotice: "Limited item. If you cannot withdraw it, you keep it and can sell it for its value.",
	substituteBadge: "Cannot be shipped",
	autoClaimLabel: "Auto-claim future opens",
	errorClaimFailed: "Claiming failed. Please try again.",
	totalValue: "Total value",
	loading: "Loading…",
	demoBadge: "Demo",
	errorGeneric: "Something went wrong. Please try again.",
	errorInsufficientBalance: "Not enough balance to open this box.",
	errorAccountLocked: "This account is locked. Contact support.",
	provablyFair: "Provably fair",
	serverSeedHash: "Server seed hash",
	clientSeed: "Client seed",
	salt: "Salt",
	seedDate: "Seed date",
	nonce: "Nonce",
	result: "Result",
	noOpensYet: "Open the box to record provably-fair rolls here.",
	battleStartsIn: "Battle starts in",
	battleGoodLuck: "Good luck!",
	battleGo: "GO",
	battleRound: "Round"
};
function d(e) {
	return e ? {
		...u,
		...e
	} : u;
}
//#endregion
//#region ../api-contract/src/storefrontThemeVars.ts
var f = /^(#[0-9a-fA-F]{3,8}|(?:oklch|rgba?|hsla?)\([0-9a-zA-Z.%,/\s+-]+\)|[a-zA-Z]{3,20})$/, p = /^(\d{1,3}(?:\.\d+)?)(px|rem|em|%)?$/, m = /^[\w\s,'"-]{1,100}$/;
function h(e) {
	let t = e?.trim() ?? "";
	return t && f.test(t) ? t : null;
}
function g(e) {
	let t = p.exec(e?.trim() ?? "");
	return t ? t[2] ? t[0] : `${t[1]}px` : null;
}
function _(e) {
	let t = e?.trim() ?? "";
	return t && m.test(t) ? t : null;
}
//#endregion
//#region ../api-contract/src/svgIcon.ts
function v(e) {
	return `data:image/svg+xml,${encodeURIComponent(e)}`;
}
//#endregion
//#region src/theme/rarity.ts
var y = {
	0: "179,179,179",
	1: "34,197,94",
	2: "59,130,246",
	3: "168,85,247",
	4: "249,115,22",
	5: "249,115,22"
}, b = {
	0: "#A9A9CA",
	1: "#22C55E",
	2: "#3B82F6",
	3: "#A855F7",
	4: "#FFC700",
	5: "#FFC700"
}, x = "#FFFFFF";
function S(e, t, n, r) {
	return `drop-shadow(0 0 ${n} rgba(${e[t] || "255,255,255"},${r}))`;
}
function C(e, t) {
	switch (t) {
		case 0: return S(e, t, "15px", "0.3");
		case 1: return S(e, t, "15px", "0.4");
		case 2: return S(e, t, "15px", "0.7");
		case 3:
		case 4:
		case 5: return S(e, t, "15px", "1");
		default: return S(e, t, "15px", "1");
	}
}
function w(e, t) {
	return t == null ? x : e[t] ?? "#FFFFFF";
}
//#endregion
//#region src/theme/theme.ts
var T = {
	background: "#0e0f1a",
	surface: "#131420",
	border: "#2a2b3d",
	text: "#ffffff",
	textMuted: "#a9a9ca",
	accent: "#8b5cf6",
	accentForeground: "#ffffff"
};
function E(e) {
	let t = e.trim();
	if (/^\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}$/.test(t)) return t.replace(/\s+/g, "");
	let n = t.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1];
	if (n) {
		let e = n.length === 3 ? n.replace(/./g, (e) => e + e) : n;
		return `${Number.parseInt(e.slice(0, 2), 16)},${Number.parseInt(e.slice(2, 4), 16)},${Number.parseInt(e.slice(4, 6), 16)}`;
	}
	let r = t.match(/^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})/i);
	return r ? `${r[1]},${r[2]},${r[3]}` : null;
}
function D(e) {
	let [t = 0, n = 0, r = 0] = e.split(",").map((e) => Number.parseInt(e, 10)), i = (e) => Math.max(0, Math.min(255, e)).toString(16).padStart(2, "0");
	return `#${i(t)}${i(n)}${i(r)}`;
}
var O = [
	0,
	1,
	2,
	3,
	4,
	5
], k = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i, A = /^[a-z][a-zA-Z0-9]{0,31}$/;
function j(e) {
	let t = {
		...T,
		...e?.colors
	}, n = { ...y }, r = { ...b };
	for (let t of O) {
		let i = e?.rarity?.[t];
		if (!i) continue;
		let a = E(i);
		a && (n[t] = a, r[t] = D(a));
	}
	let i = {};
	for (let [t, n] of Object.entries(e?.kind ?? {})) A.test(t) && typeof n == "string" && k.test(n) && (i[t] = n);
	return {
		cssVars: {
			"--los-bg": t.background,
			"--los-surface": t.surface,
			"--los-border": t.border,
			"--los-text": t.text,
			"--los-text-muted": t.textMuted,
			"--los-accent": t.accent,
			"--los-accent-foreground": t.accentForeground,
			"--los-radius": e?.radius ?? "16px",
			"--los-font": e?.fontFamily ?? "inherit",
			...Object.fromEntries(Object.entries(i).map(([e, t]) => [`--los-kind-${e}`, t]))
		},
		colors: t,
		rarityRgb: n,
		railHex: r,
		kind: i
	};
}
function M(e, t) {
	if (!t) return e;
	let n = { ...e?.colors };
	for (let [e, r] of Object.entries(t.colors ?? {})) {
		let t = typeof r == "string" ? h(r) : null;
		t && (n[e] = t);
	}
	let r = { ...e?.rarity };
	for (let e of O) {
		let n = t.rarity?.[String(e)], i = typeof n == "string" ? h(n) : null;
		i && (r[e] = i);
	}
	let i = g(t.radius) ?? e?.radius, a = _(t.fontFamily) ?? e?.fontFamily, o = {
		...e?.kind,
		...t.kind
	};
	return {
		...Object.keys(n).length ? { colors: n } : {},
		...Object.keys(r).length ? { rarity: r } : {},
		...i ? { radius: i } : {},
		...a ? { fontFamily: a } : {},
		...Object.keys(o).length ? { kind: o } : {}
	};
}
function N(e, t) {
	return Object.fromEntries(Object.entries(t).map(([t, n]) => [t, e.kind[t] ?? ("role" in n ? e.colors[n.role] : n.color)]));
}
//#endregion
//#region src/react/LootBoxContext.tsx
var P = () => {}, F = {
	strings: u,
	theme: j(),
	sounds: {
		playTickSound: P,
		playRaritySound: P,
		unlock: P,
		enabled: !1,
		configured: !1,
		muted: !1,
		gain: 1,
		subscribe: () => P
	},
	currency: i
}, I = e(F);
function L() {
	return t(I);
}
//#endregion
//#region src/core/dropTable.ts
var R = 1e6;
function z(e, t) {
	let n = e / t;
	return n === Infinity ? 0 : n > .7 && n < 1 ? 1 : n >= 1 && n < 1.5 ? 2 : n >= 1.5 && n < 6.5 ? 3 : n >= 6.5 ? 4 : 0;
}
function B(e) {
	let t = (e || 0) / R * 100;
	return Number(t.toFixed(4));
}
function V(e, t) {
	let n = 0;
	for (let t = 0; t < e.length; t++) n += e[t]?.weight ?? 0;
	if (n !== 1e6) throw Error(`[loot-os] drop-table weights must sum to ${R}, got ${n}`);
	let r = t;
	for (let t = 0; t < e.length; t++) if (r -= e[t]?.weight ?? 0, r < 0) return t;
}
//#endregion
//#region src/battle/seed.ts
function H(e) {
	let t = 2166136261;
	for (let n = 0; n < e.length; n++) t ^= e.charCodeAt(n), t = Math.imul(t, 16777619);
	return t | 0;
}
function U(e, t = 0) {
	return r(H(e) + t);
}
//#endregion
//#region src/sounds/useOpenerSoundLevel.ts
function W(e) {
	return {
		muted: n(e.subscribe, () => e.muted, () => e.muted),
		gain: n(e.subscribe, () => e.gain, () => e.gain)
	};
}
//#endregion
//#region src/openers/kit/midi.ts
var G = (e) => 440 * 2 ** ((e - 69) / 12), K = {
	level: .8,
	compressor: {
		threshold: -14,
		ratio: 4
	}
}, q = [
	0,
	2,
	4,
	7,
	9,
	12,
	14,
	16,
	19,
	21,
	24
];
function J(e) {
	let t = 1024, n = new Float32Array(t), r = Math.tanh(e);
	for (let i = 0; i < t; i++) {
		let t = i / 1023 * 2 - 1;
		n[i] = Math.tanh(t * e) / r;
	}
	return n;
}
function Y(e, t) {
	let n = Math.floor(e.sampleRate * t), r = e.createBuffer(2, n, e.sampleRate);
	for (let e = 0; e < 2; e++) {
		let t = r.getChannelData(e), i = 0;
		for (let e = 0; e < n; e++) {
			let r = e / n;
			i += (Math.random() * 2 - 1 - i) * (.9 - .75 * r), t[e] = i * (1 - r) ** 2.6;
		}
	}
	return r;
}
var X = class {
	ctx = null;
	noiseBuf = null;
	mute = null;
	volumeNode = null;
	muted = !1;
	volume = 1;
	failed = !1;
	listening = !1;
	listen() {
		if (this.listening || typeof window > "u") return;
		this.listening = !0;
		let e = () => this.unlock();
		window.addEventListener("pointerdown", e, { capture: !0 }), window.addEventListener("keydown", e, { capture: !0 });
	}
	ensure() {
		if (this.ctx || this.failed) return this.ctx;
		this.listen();
		try {
			let e = new AudioContext(), t = e.createGain();
			t.gain.value = +!this.muted;
			let n = e.createGain();
			n.gain.value = this.volume, t.connect(n).connect(e.destination);
			let r = e.sampleRate * 2, i = e.createBuffer(1, r, e.sampleRate), a = i.getChannelData(0);
			for (let e = 0; e < r; e++) a[e] = Math.random() * 2 - 1;
			this.ctx = e, this.mute = t, this.volumeNode = n, this.noiseBuf = i;
		} catch {
			this.ctx = null, this.failed = !0;
		}
		return this.ctx;
	}
	unlock() {
		let e = this.ensure();
		e && e.state === "suspended" && e.resume().catch(() => {});
	}
	setMuted(e) {
		this.muted = e, this.listen(), this.mute && this.ctx && this.mute.gain.setTargetAtTime(+!e, this.ctx.currentTime, .02);
	}
	setVolume(e) {
		Number.isFinite(e) && (this.volume = Math.min(1, Math.max(0, e)), this.volumeNode && this.ctx && this.volumeNode.gain.setTargetAtTime(this.volume, this.ctx.currentTime, .02));
	}
	ready() {
		let e = this.ensure();
		return !e || this.muted || e.state === "closed" ? null : e;
	}
}, Z = class {
	engine;
	opts;
	out = null;
	input = null;
	verb = null;
	constructor(e, t) {
		this.engine = e, this.opts = t;
	}
	build(e) {
		if (this.out) return !0;
		let t = this.engine.mute;
		if (!t) return !1;
		let { compressor: n } = this.opts, r = e.createDynamicsCompressor();
		r.threshold.value = n.threshold, r.ratio.value = n.ratio, n.knee !== void 0 && (r.knee.value = n.knee), n.attack !== void 0 && (r.attack.value = n.attack), n.release !== void 0 && (r.release.value = n.release);
		let i = e.createGain();
		if (i.gain.value = this.opts.level, i.connect(r).connect(t), this.opts.reverb) {
			let t = e.createConvolver();
			t.buffer = Y(e, this.opts.reverb.seconds);
			let n = e.createGain();
			n.gain.value = this.opts.reverb.level, n.connect(t).connect(i), this.verb = n;
		}
		return this.out = i, this.input = this.newInput(e), !0;
	}
	newInput(e) {
		if (!this.out) return null;
		let t = e.createGain();
		if (this.opts.saturation) {
			let n = e.createWaveShaper();
			n.curve = J(this.opts.saturation), n.oversample = "2x", t.connect(n).connect(this.out);
		} else t.connect(this.out);
		return t;
	}
	cut() {
		let e = this.engine.ctx, t = this.input;
		!e || !t || (t.gain.setTargetAtTime(0, e.currentTime, .03), setTimeout(() => t.disconnect(), 400), this.input = this.newInput(e));
	}
	ready() {
		let e = this.engine.ready();
		return !e || !this.build(e) || !this.input ? null : e;
	}
	chain(e, t, n) {
		let r = this.input;
		if (!r) return null;
		let i = e.currentTime + (t.when ?? 0), a = t.vol ?? .3, o = e.createGain();
		o.gain.setValueAtTime(1e-4, i);
		let s, c;
		if (t.decay !== void 0) {
			let e = Math.max(.001, t.attack ?? .003);
			s = i + e + t.decay, c = t.decay, o.gain.linearRampToValueAtTime(a, i + e);
		} else c = t.dur ?? .2, s = i + c, o.gain.exponentialRampToValueAtTime(a, i + (t.attack ?? n));
		o.gain.exponentialRampToValueAtTime(1e-4, s);
		let l = o;
		if (t.pan) {
			let n = e.createStereoPanner();
			n.pan.value = t.pan, o.connect(n), l = n;
		}
		if (l.connect(r), t.wet && this.verb) {
			let n = e.createGain();
			n.gain.value = t.wet, l.connect(n).connect(this.verb);
		}
		return {
			input: o,
			t: i,
			end: s,
			glide: t.glide ?? c
		};
	}
	tone(e) {
		let t = this.ready();
		if (!t) return;
		let n = this.chain(t, e, .004);
		if (!n) return;
		let r = t.createOscillator();
		if (r.type = e.type ?? "sine", r.frequency.setValueAtTime(e.freq, n.t), e.to && r.frequency.exponentialRampToValueAtTime(Math.max(1, e.to), n.t + n.glide), e.detune && (r.detune.value = e.detune), e.vib) {
			let i = t.createOscillator(), a = t.createGain();
			i.frequency.value = e.vib[0], a.gain.value = e.vib[1], i.connect(a).connect(r.detune), i.start(n.t), i.stop(n.end + .05);
		}
		let i = r;
		if (e.filter) {
			let n = t.createBiquadFilter();
			n.type = "lowpass", n.frequency.value = e.filter, r.connect(n), i = n;
		}
		i.connect(n.input), r.start(n.t), r.stop(n.end + .05);
	}
	noise(e) {
		let t = this.ready(), n = this.engine.noiseBuf;
		if (!t || !n) return;
		let r = this.chain(t, e, .005);
		if (!r) return;
		let i = t.createBufferSource();
		i.buffer = n, i.loop = !0;
		let a = t.createBiquadFilter();
		a.type = e.type ?? "bandpass", a.frequency.setValueAtTime(e.freq ?? 1e3, r.t), e.to && a.frequency.exponentialRampToValueAtTime(Math.max(20, e.to), r.t + r.glide), a.Q.value = e.q ?? 1, i.connect(a).connect(r.input), i.start(r.t, Math.random() * 1.5), i.stop(r.end + .05);
	}
}, Q = new class {
	engine = new X();
	main = new Z(this.engine, K);
	lastTick = 0;
	listeners = /* @__PURE__ */ new Set();
	get isMuted() {
		return this.engine.muted;
	}
	setMuted(e) {
		this.engine.setMuted(e);
		for (let e of this.listeners) e();
	}
	setVolume(e) {
		this.engine.setVolume(e);
	}
	subscribe(e) {
		return this.engine.listen(), this.listeners.add(e), () => {
			this.listeners.delete(e);
		};
	}
	unlock() {
		this.engine.unlock();
	}
	bus(e) {
		return new Z(this.engine, e);
	}
	tone(e) {
		this.main.tone(e);
	}
	noise(e) {
		this.main.noise(e);
	}
	tick(e = 1, t = .18) {
		let n = performance.now();
		n - this.lastTick < 28 || (this.lastTick = n, this.tone({
			freq: 2100 * e,
			to: 1400 * e,
			type: "triangle",
			dur: .035,
			vol: t
		}), this.noise({
			freq: 5e3,
			q: 2,
			dur: .02,
			vol: t * .6,
			type: "highpass"
		}));
	}
	click(e = .2) {
		this.tone({
			freq: 900,
			to: 500,
			type: "square",
			dur: .03,
			vol: e * .5,
			filter: 3e3
		});
	}
	pop(e = 1, t = .25) {
		this.tone({
			freq: 380 * e,
			to: 900 * e,
			type: "sine",
			dur: .09,
			vol: t
		});
	}
	whoosh(e = .4, t = .25, n = !0) {
		this.noise({
			dur: e,
			vol: t,
			type: "bandpass",
			freq: n ? 400 : 2600,
			to: n ? 2600 : 350,
			q: 1.2,
			attack: e * .5
		});
	}
	thud(e = .6) {
		this.tone({
			freq: 120,
			to: 38,
			type: "sine",
			dur: .45,
			vol: e
		}), this.noise({
			dur: .18,
			vol: e * .5,
			type: "lowpass",
			freq: 600,
			to: 120
		});
	}
	slam(e = .9) {
		this.tone({
			freq: 90,
			to: 28,
			type: "sine",
			dur: .9,
			vol: e
		}), this.tone({
			freq: 180,
			to: 50,
			type: "triangle",
			dur: .25,
			vol: e * .4
		}), this.noise({
			dur: .5,
			vol: e * .55,
			type: "lowpass",
			freq: 2400,
			to: 150
		}), this.noise({
			dur: .08,
			vol: e * .4,
			type: "highpass",
			freq: 3e3
		});
	}
	crack(e = .5) {
		this.noise({
			dur: .07,
			vol: e,
			type: "highpass",
			freq: 2200,
			q: .7
		}), this.noise({
			dur: .2,
			vol: e * .5,
			type: "bandpass",
			freq: 900,
			to: 300,
			q: 3
		}), this.tone({
			freq: 220,
			to: 70,
			type: "triangle",
			dur: .12,
			vol: e * .5
		});
	}
	shatter(e = .6) {
		for (let t = 0; t < 9; t++) this.tone({
			freq: 2400 + Math.random() * 3e3,
			type: "sine",
			dur: .15 + Math.random() * .35,
			vol: e * .12,
			when: t * .025 + Math.random() * .03,
			pan: Math.random() * 1.6 - .8
		});
		this.noise({
			dur: .5,
			vol: e * .5,
			type: "highpass",
			freq: 3500,
			to: 1500
		}), this.thud(e * .6);
	}
	tear(e = .3) {
		for (let t = 0; t < 5; t++) this.noise({
			dur: .04 + Math.random() * .04,
			vol: e * (.5 + Math.random() * .5),
			type: "bandpass",
			freq: 1800 + Math.random() * 2500,
			q: 1.5,
			when: t * .02
		});
	}
	ratchet(e = .25) {
		this.noise({
			dur: .03,
			vol: e,
			type: "bandpass",
			freq: 2600,
			q: 5
		}), this.tone({
			freq: 300,
			to: 180,
			type: "square",
			dur: .03,
			vol: e * .3,
			filter: 1800
		});
	}
	blip(e = 0, t = .2, n = "triangle") {
		let r = 72 + (q[(e % q.length + q.length) % q.length] ?? 0);
		this.tone({
			freq: G(r),
			type: n,
			dur: .14,
			vol: t
		});
	}
	riser(e = 1.5, t = .18, n = 180, r = 1400) {
		this.tone({
			freq: n,
			to: r,
			type: "sawtooth",
			dur: e,
			vol: t,
			attack: e * .8,
			filter: 2200
		}), this.noise({
			dur: e,
			vol: t * .7,
			type: "bandpass",
			freq: 500,
			to: 5e3,
			q: 2,
			attack: e * .85
		});
	}
	sparkle(e = 6, t = .1) {
		for (let n = 0; n < e; n++) this.tone({
			freq: G(84 + (q[Math.floor(Math.random() * q.length)] ?? 0)),
			type: "sine",
			dur: .25,
			vol: t,
			when: n * .06 + Math.random() * .03,
			pan: Math.random() * 1.4 - .7
		});
	}
	coin(e = .2) {
		this.tone({
			freq: G(88),
			type: "square",
			dur: .08,
			vol: e * .5,
			filter: 5e3
		}), this.tone({
			freq: G(95),
			type: "square",
			dur: .3,
			vol: e * .5,
			filter: 5e3,
			when: .07
		});
	}
	reveal(e) {
		let t = 60 + e * 2, n = [
			0,
			4,
			7,
			12,
			16,
			19
		].slice(0, 2 + Math.min(e, 4)), r = e >= 4 ? .07 : .09;
		if (n.forEach((n, i) => {
			this.tone({
				freq: G(t + n),
				type: "triangle",
				dur: .5 + e * .12,
				vol: .18,
				when: i * r
			}), e >= 2 && this.tone({
				freq: G(t + n + 12),
				type: "sine",
				dur: .6,
				vol: .07,
				when: i * r
			});
		}), e >= 3) {
			for (let e of [-8, 8]) this.tone({
				freq: G(t),
				type: "sawtooth",
				dur: 1.6,
				vol: .06,
				attack: .3,
				detune: e,
				filter: 1400
			}), this.tone({
				freq: G(t + 7),
				type: "sawtooth",
				dur: 1.6,
				vol: .05,
				attack: .3,
				detune: e,
				filter: 1400
			});
			this.sparkle(6 + e * 2, .06);
		}
		if (e >= 4) {
			this.tone({
				freq: 70,
				to: 35,
				type: "sine",
				dur: 1.2,
				vol: .6
			}), this.noise({
				dur: 1.8,
				vol: .12,
				type: "highpass",
				freq: 6e3,
				attack: .05
			});
			for (let e of [
				24,
				28,
				31,
				36
			]) this.tone({
				freq: G(t + e),
				type: "sine",
				dur: 1.4,
				vol: .05,
				when: .35,
				attack: .2
			});
		}
		if (e >= 5) {
			this.slam(.5), this.riser(.6, .1, 600, 3e3);
			for (let e = 0; e < 3; e++) this.sparkle(8, .05);
		}
	}
	hint(e) {
		this.tone({
			freq: G(55 + e * 3),
			type: "sine",
			dur: .5,
			vol: .2,
			attack: .02
		}), this.tone({
			freq: G(67 + e * 3),
			type: "triangle",
			dur: .4,
			vol: .08,
			when: .03
		});
	}
}();
//#endregion
export { i as C, l as D, s as E, d as S, c as T, S as _, H as a, v as b, z as c, L as d, M as f, y as g, b as h, U as i, V as l, j as m, G as n, R as o, N as p, W as r, B as s, Q as t, I as u, C as v, a as w, u as x, w as y };

//# sourceMappingURL=sfx-CKGwEQrr.js.map